import React, { useState, useEffect } from 'react';
import { RadarChart as RechartsRadar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend, ResponsiveContainer } from 'recharts';
import './RadarChart.css';
import { racePressure, riskLevel, isBrake } from '../utils/dimensions';
import PixelIcon from './PixelIcon';

const SHORT_NAMES = {
  compute_control: 'Compute',
  model_governance: 'Governance',
  military_integration: 'Military',
  centralization: 'Centralization',
  economic_dependency: 'Economy',
  international_enforcement: 'Cooperation',
  public_narrative: 'Narrative'
};

function RadarChart({ dimensions }) {
  const [typedLines, setTypedLines] = useState([]);
  
  // Plot race pressure: brakes are inverted, so a bigger shape always means more race
  const data = Object.entries(dimensions).map(([key, dim]) => ({
    dimension: SHORT_NAMES[key] || dim.name,
    value: parseFloat(racePressure(key, dim.value).toFixed(2)),
    fullMark: 3
  }));

  // Effect to simulate typing terminal output
  useEffect(() => {
    const lines = Object.entries(dimensions).map(([key, dim]) => {
      const risk = riskLevel(racePressure(key, dim.value));
      return {
        text: `> ${dim.name.toUpperCase()}${isBrake(key) ? ' (BRAKE)' : ''}... [${risk.label}]`,
        value: dim.value.toFixed(2),
        trend: dim.trend.toUpperCase(),
        color: risk.className
      };
    });
    setTypedLines(lines);
  }, [dimensions]);

  return (
    <div className="radar-chart-container">
      <div className="chart-title glow-text">
        <span className="title-icon"><PixelIcon name="antenna" size={18} /></span>
        DIMENSIONAL ANALYSIS
      </div>
      
      <div className="chart-wrapper">
        <div className="radar-view">
          <ResponsiveContainer width="100%" height="100%">
            <RechartsRadar outerRadius="68%">
            <PolarGrid 
              stroke="rgba(120, 170, 230, 0.3)" 
              strokeWidth={1}
              strokeDasharray="5 5"
            />
            <PolarAngleAxis 
              dataKey="dimension" 
              tick={{ 
                fill: '#a8b2d1', 
                fontSize: 12,
                fontFamily: "'JetBrains Mono', monospace"
              }}
              stroke="rgba(120, 170, 230, 0.3)"
            />
            <PolarRadiusAxis 
              angle={90} 
              domain={[0, 3]} 
              tick={{ 
                fill: '#a8b2d1', 
                fontSize: 11,
                fontFamily: "'JetBrains Mono', monospace"
              }}
              stroke="rgba(120, 170, 230, 0.3)"
            />
            <Radar
              name="Race pressure (0-3)"
              dataKey="value"
              stroke="#ff6b8f"
              fill="#ff6b8f"
              fillOpacity={0.18}
              strokeWidth={2}
              dot={{ fill: '#ff6b8f', strokeWidth: 0, r: 4 }}
              data={data}
            />
            <Legend 
              wrapperStyle={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: '12px',
                color: '#ffffff'
              }}
            />
            </RechartsRadar>
          </ResponsiveContainer>
        </div>
      </div>
      
      <p className="chart-note">
        How hard each dimension pushes toward the race. Brakes are inverted: weak governance shows as high pressure.
      </p>
      <div className="chart-legend">
        <div className="legend-item">
          <div className="legend-color" style={{ background: 'var(--accent-green)' }}></div>
          <span>&lt; 1.5 low</span>
        </div>
        <div className="legend-item">
          <div className="legend-color" style={{ background: 'var(--accent-yellow)' }}></div>
          <span>1.5-2.2 elevated</span>
        </div>
        <div className="legend-item">
          <div className="legend-color" style={{ background: 'var(--accent-red)' }}></div>
          <span>&gt; 2.2 high</span>
        </div>
      </div>

      {/* System Log */}
      <div className="analysis-terminal pixel-border-thin">
        <div className="terminal-header">
          <span className="blink">_</span> SYSTEM_LOG://DIMENSIONAL_SCAN
        </div>
        <div className="terminal-content custom-scrollbar">
          {typedLines.map((line, idx) => (
            <div key={idx} className="terminal-line">
              <span className="timestamp">{new Date().toLocaleTimeString()}</span>
              <span className={`status ${line.color}`}>{line.text}</span>
              <span className="metrics">
                VAL:{line.value} / TRD:{line.trend}
              </span>
            </div>
          ))}
          <div className="terminal-line">
            <span className="timestamp">{new Date().toLocaleTimeString()}</span>
            <span className="text-cyan">{'> CYCLE COMPLETE. AWAITING DATA...'}</span>
            <span className="cursor-block">█</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RadarChart;
